import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Box } from "@chakra-ui/react";
import { getLocale } from "src/utils/locale";
import time from "src/utils/time";
import BabyLottieAnimation from "src/modules/avatar/components/BabyLottieAnimation";
import DogAvatar from "src/modules/avatar/components/DogAvatar";
import { DeviceName } from "src/modules/device/components/DeviceSpec";
import { BarkioOnly, BibinoOnly } from "src/utils/environment";
import { Lottie } from "src/components/Lottie";
import { H2 } from "src/atoms/typography";
import getAnimationData from "src/utils/getAnimationData";
import AnimationBox from "../../components/AnimationBox";
import LastEventSubtitle from "../../components/LastEventSubtitle";
import { AvatarModes } from "src/modules/avatar/enum";
import { AvatarProfile, SmallAvatarSleeping } from "../../components/AvatarContainers";

const HOUR_IN_MS = 3600000;

export default function Sleeping({
  setAnimationControls,
  toggleActivityLog,
  lastMotionBefore,
  showSmallAvatar,
  lastNoiseBefore,
  selectedAvatar,
  isInDetachMode,
  isInTileView,
  isMultiRoom,
  avatarSize,
  jid
}) {
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      if (setAnimationControls) setAnimationControls(null);
    };
  }, []);

  const syncAnim = (controls) => {
    if (setAnimationControls) {
      controls.stop();
      setAnimationControls(controls);
    }
  };
  const lastNoiseInMs = lastNoiseBefore;
  const lastMotionInMs = lastMotionBefore;

  const renderLastNoise =
    (!lastMotionBefore && lastNoiseBefore) || (lastMotionBefore && lastNoiseBefore && lastMotionInMs > lastNoiseInMs);

  const renderLastMotion =
    (lastMotionBefore && !lastNoiseBefore) || (lastMotionBefore && lastNoiseBefore && lastMotionInMs < lastNoiseInMs);
  const displayProfileImage =
    selectedAvatar && selectedAvatar.previewMode === AvatarModes.profileImage && selectedAvatar.profileImageFile;
  const isRo = getLocale().split("-")[0] === "ro";

  return (
    <Flex
      width="100%"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      {...(!isInDetachMode && { flex: "1 1 auto" })}
      // pb={isInDetachMode ? "0px" : ["32px", "48px", "56px"]}
      pb={isInDetachMode ? "0px" : "10px"}
    >
      <Box
        sx={{
          "@media (max-height: 140px)": {
            display: "none"
          }
        }}
      >
        {showSmallAvatar && <SmallAvatarSleeping avatar={selectedAvatar} avatarSize={avatarSize} syncAnim={syncAnim} />}
        {!showSmallAvatar && displayProfileImage && <AvatarProfile avatar={selectedAvatar} includeZzz />}
        {!showSmallAvatar && !displayProfileImage && (
          <AnimationBox>
            <BibinoOnly
              sex={selectedAvatar?.sex}
              {...selectedAvatar?.avatarCustomization}
              skinColor={selectedAvatar?.avatarCustomization?.skinColor || selectedAvatar?.moreDataJson?.skinColor}
              animationName="sleeping.json"
              sx={{
                preserverAspectRation: "xMidYMax meet"
              }}
              getAnimControls={(controls) => syncAnim(controls)}
            >
              {BabyLottieAnimation}
            </BibinoOnly>
            <BarkioOnly imageName={`sleeping-${selectedAvatar?.avatarCustomization?.breedAppearance || "default"}.svg`}>
              {DogAvatar}
            </BarkioOnly>
          </AnimationBox>
        )}
      </Box>
      {isMultiRoom && <DeviceName deviceJid={jid} />}

      <Box
        display="none"
        sx={{
          "@media (max-height: 140px)": {
            display: "inline-block",
            mb: "8px"
          }
        }}
      >
        <AnimationBox>
          <Lottie width="100%" height="100%" animationData={getAnimationData("zzz-small.json")} />
        </AnimationBox>
      </Box>
      <H2
        // fontSize={isInDetachMode ? "6.9vmin" : ["20px", "28px", "32px"]}
        fontSize={isInDetachMode ? "6.9vmin" : "clamp(2rem, 3.82vmin, 3.2rem)"}
        // lineHeight={isInDetachMode ? "8.7vmin" : ["24px", "32px", "40px"]}
        letterSpacing={["0.24px", "0.34px", "0.38px"]}
        color="highEmphasis"
        mt={isInDetachMode ? "1.75vmin" : "16px"}
        {...(isInDetachMode && {
          sx: {
            "@media (max-height: 140px)": {
              fontSize: "10px !important",
              lineHeight: "1.1 !important"
            }
          }
        })}
        flex="0 0 auto"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxWidth={isInDetachMode ? "90vw" : isInTileView ? "calc(100% + 102px)" : "60vw"}
      >
        {selectedAvatar
          ? t(selectedAvatar.sex === "male" ? "maleIsSleeping" : "femaleIsSleeping", { value0: selectedAvatar.name })
          : t("avatarIsSleeping")}
      </H2>
      {renderLastNoise && (
        <LastEventSubtitle
          isInTileView={isInTileView}
          alignItems="center"
          mt={isInDetachMode ? "1vmin" : "8px"}
          onClick={toggleActivityLog}
          color="mediumEmphasis"
          subtitleCss={{
            flex: "0 0 auto",
            sx: {
              "@media (max-height: 140px)": {
                display: "none !important"
              }
            }
          }}
          subtitle={t("parentStation.monitor.lastNoise", {
            value0:
              isRo || lastNoiseInMs > HOUR_IN_MS
                ? time(Date.now() - lastNoiseInMs).format("HH:mm")
                : lastNoiseInMs < 60000
                ? t("activityLog.momentAgo.text")
                : time.duration(-lastNoiseBefore).humanize()
          })}
        />
      )}
      {renderLastMotion && (
        <LastEventSubtitle
          isInTileView={isInTileView}
          alignItems="center"
          mt={isInDetachMode ? "0px" : "8px"}
          onClick={toggleActivityLog}
          color="mediumEmphasis"
          subtitleCss={{
            flex: "0 0 auto",
            sx: {
              "@media (max-height: 140px)": {
                display: "none !important"
              }
            }
          }}
          subtitle={t("monitor.lastMotion", {
            value0:
              isRo || lastMotionInMs > HOUR_IN_MS
                ? time(Date.now() - lastMotionInMs).format("HH:mm")
                : lastMotionInMs < 60000
                ? t("activityLog.momentAgo.text")
                : time.duration(-lastMotionBefore).humanize()
          })}
        />
      )}
    </Flex>
  );
}
